import { isArray, isEqual, isNil, xorWith } from 'lodash';

/**
 * Returns property name in string.
 * @param f function to access property
 * @example
 *    interface Example { isValid: boolean }
 *    console.log(nameOf((e: Example) => e.isValid)) // output: "isValid"
 */
export function nameOf(f: (x: any) => any): string {
  // Reference https://stackoverflow.com/questions/50470025/nameof-keyword-in-typescript
  const proxy = new Proxy(
    {},
    {
      get: (target, key) => key,
    },
  );
  return f(proxy);
}

// Convert generic object to array of object
export const convertObjectToArray = <T>(object: T | T[]): T[] => {
  if (isNil(object)) return [];

  // If object is already an object, simply return it. Othwerise, convert it to an array
  return Array.isArray(object) ? object : [object];
};

/**
 * Returns true if value and ref is considered equal
 * isEqual from lodash will return false if two arrays contains the same content but order differs. This
 * why we use a different check for arrays
 */
export function isSameContent<T>(value1: T, value2: T): boolean {
  // When comparing anything exept arrays, use the isEqual comparator
  if (!isArray(value1) || !isArray(value2)) return isEqual(value1, value2);
  // xorWith find the symmetric difference on two arrays (find elements that are not in the intersection of two arrays)
  // If all elements of an array is in the intersection, it means that all items are equal
  return xorWith(value1, value2, isSameContent).length === 0;
}

// Compute all combinations from an array without the duplicates
// @input   [x, y, z]
// @output  [x], [x,y], [x,y,z], [y], [y,z], [z]
export function computeCombinations<T>(values: T[]): T[][] {
  const combinations: T[][] = [];
  for (let i = 0; i < values.length; i++) {
    for (let j = i + 1; j <= values.length; j++) {
      const combination = values.slice(i, j);
      combinations.push(combination);
    }
  }

  return combinations;
}
